/**
 * Gift-card-specific detail sections: key-facts strip, Gift Card section (with
 * audited number/PIN reveal), Association, and Customer/Supplier rows. The
 * label/value rows are react-admin <SimpleShowLayout> blocks; composite/derived
 * values use <FunctionField>, and the audited reveal control is a bespoke
 * value wrapped in <Labeled>. Presents the same fields as reware-admin's gift
 * card detail.
 */
import { useState } from 'react';
import { FunctionField, Labeled, SimpleShowLayout, useNotify, useRefresh } from 'react-admin';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import {
  detailLayoutSx,
  displayNameFor,
  type Flag,
  FlagChips,
  formatDate,
  type KeyFact,
  KeyFactsStrip,
  monoFieldSx,
  SectionLabel,
} from '@src/components/indexPage';

import { formatMaskedNumber, formatMoney } from './format';
import { reveal } from './mutations';
import { parseDeliveryAddress } from './types';
import type { ConstantOption, DeliveryAddress, GiftcardParent, GiftcardRecord } from './types';

// Annotated rather than inferred: the inferred type names a generated module through a relative
// path that is not portable across packages.
export const associatedModel = (gc: GiftcardRecord): GiftcardParent | null | undefined =>
  gc.order ?? gc.tradein;

/**
 * The parent's delivery address, parsed. Shared so the four read sites cannot each re-derive the
 * shape of an unvalidated JSON scalar — null means "could not read", not "no details recorded".
 */
export const deliveryAddressOf = (gc: GiftcardRecord): DeliveryAddress | null =>
  parseDeliveryAddress(associatedModel(gc)?.deliveryAddress);

/**
 * Null when the card has neither parent. Both `order_id` and `tradein_id` are nullable, and a
 * manually issued card can have neither — reporting those as 'Trade-in' invented an association
 * that does not exist.
 */
export const modelType = (gc: GiftcardRecord): 'Order' | 'Trade-in' | null => {
  if (gc.order) return 'Order';
  if (gc.tradein) return 'Trade-in';
  return null;
};

export const customerLabel = (gc: GiftcardRecord): 'Customer' | 'Supplier' =>
  (gc.order ? 'Customer' : 'Supplier');

/**
 * Shared so the list column and the key-facts strip cannot disagree — they previously showed
 * 'N/A' and a real date for the same card. The rule mirrors reware-admin's gift card detail:
 * only a card that reached issuance has an issue date to show.
 */
export const issuedDate = (gc: GiftcardRecord): string =>
  (['ISSUED', 'COMPLETE'].includes(gc.state) ? formatDate(gc.createdAt) : 'N/A');

export const GiftcardKeyFacts = ({ gc, states }: { gc: GiftcardRecord, states: ConstantOption[] }) => {
  const currency = gc.transactionCurrencyCode;
  const type = modelType(gc);
  const facts: KeyFact[] = [
    { label: 'Amount', value: formatMoney(gc.amount, currency) },
    { label: 'Remaining', value: formatMoney(gc.balanceAmount, currency) },
    { label: 'State', value: displayNameFor(states, gc.state) },
    { label: 'Issued', value: issuedDate(gc) },
  ];
  if (type) {
    facts.push({ label: type, value: associatedModel(gc)?.uuidPrefix ?? '—' });
  }
  return <KeyFactsStrip facts={facts} />;
};

/**
 * A masked value with an eye toggle that reveals the full value (audited).
 *
 * `revealed` is null until a reveal completes, then holds the result — including
 * a null result. That distinction matters: the server writes the audit
 * transition before it returns, so a card with no stored PIN would otherwise
 * leave the mask in place and log a fresh reveal on every click.
 */
const RevealValue = ({
  gc,
  field,
  masked,
}: {
  gc: GiftcardRecord;
  field: 'number' | 'pin';
  masked: string;
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [revealed, setRevealed] = useState<{ value: string | null } | null>(null);
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    if (revealed) {
      setRevealed(null);
      return;
    }
    setBusy(true);
    try {
      // Trimmed before the emptiness test. A whitespace-only value is truthy, so it would lift
      // the mask onto a blank line with no warning — reading as a render glitch, which invites
      // re-clicking, and every click writes another audit row server-side.
      const value = (await reveal(gc.id, field))?.trim() || null;
      setRevealed({ value });
      if (!value) {
        // "was returned", not "is stored": the server sends null both for an absent value and
        // for one that failed to stringify, and the client cannot tell which.
        notify(`No ${field} was returned for this gift card`, { type: 'warning' });
      }
      // The reveal wrote an audited transition. Without this the Reveal History tab does not
      // move — and for a first reveal the tab does not appear at all — so the audit the tooltip
      // promises is invisible on the one page meant to show it.
      refresh();
    } catch (error) {
      // Falls back on an empty message too, not just a non-Error: an HttpError built from a
      // missing status text is still an Error, so `instanceof` alone would notify ''.
      const message = error instanceof Error && error.message ? error.message : `Could not reveal ${field}`;
      console.error('Gift card reveal failed', { giftcardId: gc.id, field, error });
      notify(message, { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Typography variant="body2" sx={monoFieldSx}>
        {revealed ? revealed.value ?? '—' : masked}
      </Typography>
      <Tooltip title={revealed ? `Hide ${field}` : `Reveal ${field} (audited)`}>
        {/* aria-label has to flip with the icon: it overrides the tooltip as the accessible
            name, so a fixed "Reveal" would announce the wrong action once the value is shown. */}
        <IconButton
          size="small"
          onClick={toggle}
          disabled={busy}
          sx={{ p: 0.25 }}
          aria-label={revealed ? `Hide ${field}` : `Reveal ${field} (audited)`}
        >
          {revealed ? <VisibilityOffIcon sx={{ fontSize: 15 }} /> : <VisibilityIcon sx={{ fontSize: 15 }} />}
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

export const GiftCardSection = ({
  gc,
  sources,
  reasons,
}: {
  gc: GiftcardRecord;
  sources: ConstantOption[];
  reasons: ConstantOption[];
}) => {
  const currency = gc.transactionCurrencyCode;
  return (
    <Box>
      <SectionLabel>Gift Card</SectionLabel>
      <SimpleShowLayout sx={detailLayoutSx}>
        <Labeled label="Number">
          <RevealValue gc={gc} field="number" masked={formatMaskedNumber(gc.number)} />
        </Labeled>
        <Labeled label="PIN">
          <RevealValue gc={gc} field="pin" masked="••••" />
        </Labeled>
        <FunctionField label="Source" render={() => displayNameFor(sources, gc.source)} />
        {gc.source === 'ISSUED_MANUAL' ? (
          <FunctionField label="Reason" render={() => displayNameFor(reasons, gc.reason)} />
        ) : null}
        <FunctionField label="Starting Balance" render={() => formatMoney(gc.initialAmount, currency)} />
        <FunctionField label="Remaining Balance" render={() => formatMoney(gc.balanceAmount, currency)} />
        {currency && currency !== 'USD' ? <FunctionField label="Currency" render={() => currency} /> : null}
      </SimpleShowLayout>
    </Box>
  );
};

export const AssociationSection = ({ gc }: { gc: GiftcardRecord }) => {
  const model = associatedModel(gc);
  const type = modelType(gc);
  // Nothing to show for a card with no parent; the zone disappears rather than rendering a
  // section whose every value is an em dash.
  if (!type || !model) return null;
  return (
    <Box>
      <SectionLabel>Association</SectionLabel>
      <SimpleShowLayout sx={detailLayoutSx}>
        <FunctionField label="Type" render={() => type} />
        <FunctionField label={`${type} Ref`} render={() => model?.uuidPrefix ?? '—'} sx={monoFieldSx} />
        <FunctionField label={`${type} ID`} render={() => model?.id ?? '—'} sx={monoFieldSx} />
      </SimpleShowLayout>
    </Box>
  );
};

export const CustomerSection = ({ gc }: { gc: GiftcardRecord }) => {
  const address = deliveryAddressOf(gc);
  // The contact details come from the parent order or trade-in, so with no parent there is no
  // one to name — and the heading would have to guess between Customer and Supplier.
  if (!address) return null;
  return (
    <Box>
      <SectionLabel>{customerLabel(gc)}</SectionLabel>
      <SimpleShowLayout sx={detailLayoutSx}>
        <FunctionField label="Name" render={() => address?.name ?? '—'} />
        <FunctionField label="Email" render={() => address?.email ?? '—'} />
        <FunctionField label="Phone" render={() => address?.phone ?? '—'} />
      </SimpleShowLayout>
    </Box>
  );
};

/** Gift-card-level flag chips — rendered only when applicable. */
export const GiftcardFlags = ({ gc }: { gc: GiftcardRecord }) => {
  const partiallyRedeemed = gc.initialAmount != null && gc.balanceAmount != null &&
    gc.balanceAmount > 0 && gc.balanceAmount < gc.initialAmount;
  // Requires a starting balance, the same guard partiallyRedeemed has: "Depleted" claims a
  // balance was spent down, which is wrong for a card that was never funded — an AUTH or
  // PENDING_STRIPE card sits at 0 without having been used.
  const depleted = gc.initialAmount != null && gc.initialAmount > 0 &&
    gc.balanceAmount === 0 && !['CANCELED', 'CANCELED_IN_STORE', 'COMPLETE'].includes(gc.state);
  const flags: Flag[] = [
    { label: 'Partially Redeemed', color: 'warning', show: partiallyRedeemed },
    { label: 'Depleted', color: 'default', show: depleted },
    { label: 'Manual Issue', color: 'default', show: gc.source === 'ISSUED_MANUAL' },
  ];
  return <FlagChips flags={flags} />;
};
